<template>
	<div class="flex flex-col h-full">
		<div class="flex-auto overflow-scroll">			
			<div class="flex justify-between items-center content-center pt-4">
				<h3 class="text-2xl font-bold px-4">      		
					Ordenes
				</h3>

				<div class="text-left flex-none pr-4 flex flex-row space-x-4">
					<button v-if="proveedor.standalone && !selectionMode" class="rounded ring-1 ring-inset ring-primary text-primary font-bold p-1 px-4" @click="startSelection">
						Crear recolección
					</button>

					<button class="rounded bg-primary hover:bg-primary-dark text-white font-bold p-1 px-4"  @click="generateCSV()">
						Reporte (Orden)
					</button>

					<button class="rounded bg-primary hover:bg-primary-dark text-white font-bold p-1 px-4"  @click="generateCSVWithLineItems()">
						Reporte (Producto)
					</button>
				</div>
			</div>

			<div class="w-full text-left px-4 pb-2 pt-4">
				<select @change="updateQuery()" v-model="cliente" v-if="clientes.length > 0" id="countries" class="border border-gray-300 focus:border-gray-500 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
					<option selected value="">Todos los clientes</option>
					<option v-for="cliente in clientes" v-bind:key="cliente.id" :value="cliente">{{cliente.nombre}}</option>			
				</select>
			</div>

			<empty v-if="pedidos && filteredPedidos && filteredPedidos.length == 0" :texto=" cliente ? `${cliente.nombre} no tiene ordenes de compra` :'Aún no tienes ordenes de compra' "> </empty>

			<div v-if="filteredPedidos && filteredPedidos.length > 0" class="pb-4">
				<inbox
					class="mt-4"
					:user="'proveedor'"
					:ordenes="filteredPedidos"
					:fields="{cliente: true, recoleccion: proveedor.standalone, surtir: !proveedor.standalone, pago: proveedor.payments, aprobar: true, facturar: true, entregar: true}"
					:selection-mode="selectionMode"
					:selected-order-ids="selectedPedidoIds"
					:can-select-order="canSelectOrder"
					@toggle-order="toggleOrder"
				></inbox>
			</div>	
		</div>

		<div v-if="selectionMode" class="p-4 flex-none border-t border-t-gray-300 flex gap-4">
			<button class="w-1/3 border border-gray-300 rounded font-bold py-2 px-4" @click="cancelSelection">
				Cancelar
			</button>
			<button :disabled="selectedPedidoIds.length == 0 || creatingCollection" class="w-2/3 bg-primary hover:bg-primary-dark disabled:opacity-50 rounded text-white font-bold py-2 px-4" @click="createCollection">
				Crear recolección ({{ selectedPedidoIds.length }})
			</button>
		</div>

		<div v-else class="p-4 flex-none border-t border-t-gray-300">
			<router-link :to="`/proveedor/${proveedor.id}/orden${ cliente.id ? `?cliente=${cliente.id}` : ''}`">
				<button class="w-full bg-primary hover:bg-primary-dark rounded text-white font-bold py-2 px-4">
					Agregar orden
				</button>
			</router-link>
		</div>
	</div>
</template>

<script>
	import { supabase } from "../supabase.js"
	import { useRoute, useRouter } from 'vue-router'
	import { onMounted, ref, computed, watch } from "vue"
	import { useToast } from 'vue-toast-notification'
	import { utils } from "./utils/utils.js"
	import { getOrderDeliveryState, isEligibleForCollection } from "./utils/recolecciones.js"
	import { useLoading } from 'vue-loading-overlay'
	import { store } from '../store.js'

	const loading = useLoading({
		color: "#428b74",
		loader: "spinner",
		// The auth watcher can overlap the initial request. Two full-page
		// focus traps otherwise keep focusing each other until the stack overflows.
		enforceFocus: false
	});

	export default {
		name: 'Order',
		setup() {
			const route = useRoute()
			const router = useRouter()
			const proveedor = ref({})
			const pedidos = ref(null)
			const clientes = ref([])
			const cliente = ref("")		
			const selectionMode = ref(false)
			const selectedPedidoIds = ref([])
			const creatingCollection = ref(false)
			let ordersLoadPromise = null

			async function getClientes() {
				let { data, error, status } = await supabase
				.from("cliente_proveedor")
				.select('proveedor, cliente(id, nombre, contacto_correo)')				
				.match({										
					"proveedor": route.params.proveedor_id
				})

				if (data) {   					
					clientes.value = data.sort((a, b) => {
						let fa = a.cliente.nombre.toLowerCase(),
						fb = b.cliente.nombre.toLowerCase();

						if (fa < fb) {
							return -1;
						}
						if (fa > fb) {
							return 1;
						}
						return 0;
					}).map( d => { return d.cliente})
				} else if (error) {  
					if (status == 406) {
						return						
					} else {
						console.log(error)
						alert('Hubo un error')
					}
				}   

				return
			}

			const filteredPedidos = computed( () => {
				return pedidos.value ? pedidos.value.filter( p => cliente.value.id ? p.cliente.id == cliente.value.id : true ) : []
			})

			function canSelectOrder(pedido) {
				return isEligibleForCollection(pedido, proveedor.value)
			}

			function loadOrders(provider = proveedor.value) {
				if (ordersLoadPromise) return ordersLoadPromise

				ordersLoadPromise = (async () => {
					const loader = loading.show()
					try {
						pedidos.value = await utils.getPedidosPorProveedor(route.params.proveedor_id, provider.standalone === true)
					} catch (error) {
						if (error.status !== 401) {
							useToast().error(error.message, { position: "top-right" })
						}
					} finally {
						loader.hide()
						ordersLoadPromise = null
					}
				})()

				return ordersLoadPromise
			}

			function toggleOrder(pedidoId) {
				const selectedIndex = selectedPedidoIds.value.indexOf(pedidoId)
				if (selectedIndex === -1) {
					selectedPedidoIds.value.push(pedidoId)
				} else {
					selectedPedidoIds.value.splice(selectedIndex, 1)
				}
			}

			function startSelection() {
				if (!proveedor.value.direccion_recoleccion) {
					useToast().warning('Configura tu dirección de recolección para continuar', { position: "top-right" })
					return router.push(`/proveedor/${route.params.proveedor_id}/configuracion`)
				}

				selectionMode.value = true
			}

			function cancelSelection() {
				selectionMode.value = false
				selectedPedidoIds.value = []
			}

			async function createCollection() {
				creatingCollection.value = true
				try {
					const recoleccionId = await utils.crearRecoleccion(proveedor.value.id, selectedPedidoIds.value)
					const now = new Date().toISOString()
					pedidos.value.forEach((pedido) => {
						if (!selectedPedidoIds.value.includes(pedido.id)) return
						if (!pedido.recoleccion_pedidos) pedido.recoleccion_pedidos = []
						pedido.recoleccion_pedidos.push({
							recoleccion: { id: recoleccionId, estado: 'solicitada', solicitada_at: now }
						})
					})
					cancelSelection()
					useToast().success('Recolección creada correctamente', { position: "top-right" })
				} catch (error) {
					console.log(error)
					useToast().error(error.message || 'No se pudo crear la recolección', { position: "top-right" })
				} finally {
					creatingCollection.value = false
				}
			}

			function generateCSV() {
				if (filteredPedidos.value.length == 0) {
					return
				}

				let csv = "Fecha, Pedido, Cancelado, Surtido, Estado entrega, Cliente, Total"

				filteredPedidos.value.forEach( pedido => {			
					csv += `\n${pedido.created_at.split("T")[0]},${pedido.id},${pedido.cancelado},${pedido.surtido},${getOrderDeliveryState(pedido)},${pedido.cliente ? pedido.cliente.nombre.replaceAll(",","") : ""},${pedido.line_items.map(li => (li.cantidad ? li.precio * li.cantidad : 0)).reduce((a, b) => a + b)}`
				})

				const anchor = document.createElement('a');
				anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
				anchor.target = '_blank';
				anchor.download = 'ordenes.csv';
				anchor.click();
			}


			function generateCSVWithLineItems() {
				let csv = "Fecha, Pedido, Cancelado, Surtido, Estado entrega, Proveedor, Cliente, Producto, Cantidad, Total"

				pedidos.value.forEach( pedido => {
					console.log(pedido)
					pedido.line_items.forEach( lineItem => {
						csv += `\n${(new Date(pedido.created_at)).toLocaleDateString("en-GB", { timeZone: 'America/Mexico_City' })},${pedido.id},${pedido.cancelado},${pedido.surtido},${getOrderDeliveryState(pedido)},${pedido.proveedor ? pedido.proveedor.nombre.replaceAll(",","") : ""},${pedido.cliente ? pedido.cliente.nombre.replaceAll(",","") : ""},${lineItem.producto.nombre.replaceAll(",","")},${lineItem.cantidad},${ lineItem.cantidad ? lineItem.precio * lineItem.cantidad : 0 }`
					})
				})

				const anchor = document.createElement('a');
				anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
				anchor.target = '_blank';
				anchor.download = 'ordenes.csv';
				anchor.click();
			}

			function updateQuery() {
				if (cliente.value.id) {
					let data = { 
						path: `/proveedor/${route.params.proveedor_id}/ordenes`, 
						query: { cliente: cliente.value.id }
					}

					router.push(data)
				} else {
					let data = {
						path: `/proveedor/${route.params.proveedor_id}/ordenes`
					}
					router.push(data)				
				}
			}

			onMounted( async () => {
				const data = await utils.getProveedor(route.params.proveedor_id)
				proveedor.value = data
				if (store.auth) loadOrders(data)

				getClientes()
				.then( () => {
					if (route.query.cliente) {
						cliente.value = clientes.value.find( c => c.id == route.query.cliente)
					}
				})
			})

			watch(() => store.auth, (authorized) => {
				if (authorized && proveedor.value.id) loadOrders()
			})

			return {
				proveedor,
				pedidos,
				clientes,
				cliente,
				filteredPedidos,
				generateCSV,
				generateCSVWithLineItems,
				updateQuery,
				selectionMode,
				selectedPedidoIds,
				creatingCollection,
				canSelectOrder,
				toggleOrder,
				startSelection,
				cancelSelection,
				createCollection
			}
		},
	}
</script>
